import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import UniversalLookup from './UniversalLookup';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2'; 
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import ReactFlow, { 
  addEdge, 
  Background, 
  Controls, 
  Panel, 
  MiniMap,
  applyNodeChanges,
  applyEdgeChanges,
  MarkerType,
  Handle,
  Position,
  ConnectionMode, 
} from 'reactflow';
import 'reactflow/dist/style.css';
import { 
  Play, Settings, Save, Zap, AlertCircle, 
  UserCheck, GitBranch, Power, X, MousePointer2, Search, 
  AlignLeft, ArrowLeft, Trash2, History, Type, Plus, Minus, Info, FileCode
} from 'lucide-react';

// --- 1. Custom Node Components ---

const StartNode = () => (
  <div className="px-5 py-3 shadow-xl rounded-full bg-green-50 border-2 border-green-600 flex items-center justify-center min-w-[100px] relative">
    <Power size={14} className="text-green-600 mr-2" />
    <div className="text-[10px] font-black text-green-600 uppercase">START</div>
    <Handle type="source" position={Position.Bottom} className="w-3 h-3 bg-green-600 border-2 border-white" />
  </div>
);

const EndNode = () => (
  <div className="px-5 py-3 shadow-xl rounded-full bg-red-50 border-2 border-red-600 flex items-center justify-center min-w-[100px] relative">
    <Handle type="target" position={Position.Top} className="w-3 h-3 bg-red-600 border-2 border-white" />
    <X size={14} className="text-red-600 mr-2" />
    <div className="text-[10px] font-black text-red-600 uppercase">STOP</div>
  </div>
);

const TaskNode = ({ data }) => (
  <div className="px-4 py-2 shadow-lg rounded-md bg-white border-2 border-blue-600 min-w-[160px] relative">
    <Handle type="target" position={Position.Top} className="w-2 h-2 bg-blue-600" />
    <div className="flex items-center">
      <div className="rounded-full p-1 bg-blue-600 text-white shadow-sm">
        <UserCheck size={12} />
      </div>
      <div className="ml-3">
        <div className="text-[9px] font-black text-blue-600 uppercase tracking-tighter">User Task</div>
        <div className="text-xs font-bold text-gray-700">{data.label}</div>
        {data.assignee && (
          <div className="mt-1 text-[8px] px-1 bg-blue-50 text-blue-500 rounded border border-blue-100 truncate font-bold">
            👤 {data.assignee}
          </div>
        )}
      </div>
    </div>
    <Handle type="source" position={Position.Bottom} className="w-2 h-2 bg-blue-600" />
  </div>
);

const ConditionNode = ({ data }) => (
  <div className="px-4 py-3 shadow-lg rounded-md bg-white border-2 border-orange-500 min-w-[160px] relative">
    <Handle type="target" position={Position.Top} className="w-2 h-2 bg-orange-500" />
    <div className="flex items-center">
      <div className="rounded-full p-1 bg-orange-500 text-white shadow-sm">
        <GitBranch size={12} />
      </div>
      <div className="ml-3">
        <div className="text-[9px] font-black text-orange-500 uppercase tracking-tighter">Decision</div>
        <div className="text-xs font-bold text-gray-700">{data.label}</div>
      </div>
    </div>
    <Handle type="source" position={Position.Right} id="true" className="w-3 h-3 bg-green-500 border-2 border-white" />
    <Handle type="source" position={Position.Bottom} id="false" className="w-3 h-3 bg-red-500 border-2 border-white" />
  </div>
);

const nodeTypes = {
  start: StartNode,
  task: TaskNode,
  condition: ConditionNode,
  end: EndNode,
};

// --- 2. Main Designer Component ---

const WorkflowDesigner = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams(); 
  const navigate = useNavigate();
  const reactFlowWrapper = useRef(null);
  
  const [loading, setLoading] = useState(false);
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [selectedEdge, setSelectedEdge] = useState(null);
  const [fontSize, setFontSize] = useState(13);
  
  const [meta, setMeta] = useState({
    name: '',
    app: 'WORKORDER',
    description: '',
    isActive: false
  });

  const [lookup, setLookup] = useState({ isOpen: false, title: '', type: '', field: '' });

  const isRTL = i18n.language === 'ar' || i18n.language === 'ur';

  const onNodesChange = useCallback((changes) => setNodes((nds) => applyNodeChanges(changes, nds)), []);
  const onEdgesChange = useCallback((changes) => setEdges((eds) => applyEdgeChanges(changes, eds)), []);

  const loadWorkflowData = async () => {
    if (!id || id === 'new') return;
    setLoading(true);
    try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`/api/workflow/process/${id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = res.data; 

        setMeta({
            name: data.process_name || '',
            app: data.object_name || 'WORKORDER',
            description: data.description || '',
            isActive: data.is_active || false
        });

        if (data.layout_data) {
            const parsed = typeof data.layout_data === 'string' ? JSON.parse(data.layout_data) : data.layout_data;
            
            const enrichedNodes = parsed.nodes.map(node => {
                const dbNode = data.nodes.find(dn => dn.node_id === node.id);
                if (dbNode) {
                    return {
                        ...node,
                        data: {
                            ...node.data,
                            action_script: dbNode.action_script || "",
                            assignee: dbNode.assignee || node.data.assignee
                        }
                    };
                }
                return node;
            });

            setNodes(enrichedNodes);
            setEdges(parsed.edges || []);
        }
    } catch (err) {
        toast.error(t('workflow.toasts.fetch_fail'));
    } finally {
        setLoading(false);
    }
};

  useEffect(() => { loadWorkflowData(); }, [id]);

  const onConnect = useCallback((params) => {
    const isTruePath = params.sourceHandle === 'true';
    const isFalsePath = params.sourceHandle === 'false';
    const newEdge = { 
      ...params, 
      animated: true, 
      label: isTruePath ? 'TRUE' : isFalsePath ? 'FALSE' : '', 
      data: { result: isTruePath ? 'TRUE' : isFalsePath ? 'FALSE' : 'NONE' },
      style: { 
        stroke: isTruePath ? '#22c55e' : isFalsePath ? '#ef4444' : '#1e40af', 
        strokeWidth: 2,
        strokeDasharray: isFalsePath ? '5,5' : '0'
      },
      labelStyle: { fill: isTruePath ? '#22c55e' : isFalsePath ? '#ef4444' : '#1e40af', fontWeight: 900, fontSize: '10px' },
      markerEnd: { type: MarkerType.ArrowClosed, color: isTruePath ? '#22c55e' : isFalsePath ? '#ef4444' : '#1e40af' }
    };
    setEdges((eds) => addEdge(newEdge, eds));
  }, []);

  const updateNodeData = (field, value) => {
    if (!selectedNode) return;
    setNodes((nds) => nds.map((node) => node.id === selectedNode.id ? { ...node, data: { ...node.data, [field]: value } } : node));
    setSelectedNode(prev => ({ ...prev, data: { ...prev.data, [field]: value } }));
  };

  const onAddNode = (type) => {
    const newNodeId = `${type}_${+new Date()}`;
    const newNode = {
      id: newNodeId,
      type,
      position: { x: 250, y: 250 },
      data: { 
        label: `New ${type}`, 
        logic: '', 
        assignee: '', 
        escalation: 24, 
        action_script: '', 
        post_action_type: '', 
        post_action_value: '' 
      },
    };
    setNodes((nds) => nds.concat(newNode));
  };

  const handlePublish = async () => {
    if (!meta.name) return toast.error(t('workflow.toasts.id_required'));
    
    const result = await Swal.fire({
      title: t('workflow.swal.publish_title'),
      text: t('workflow.swal.publish_text'),
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#1a3353',
      cancelButtonColor: '#d33',
      confirmButtonText: t('workflow.swal.confirm_publish')
    });

    if (result.isConfirmed) {
      const loadingToast = toast.loading(t('workflow.toasts.saving'));
      try {
          const token = localStorage.getItem('token');
          await axios.post('/api/workflow/save', {
              process_name: meta.name,
              object_name: meta.app,
              description: meta.description,
              is_active: meta.isActive,
              layout_data: JSON.stringify({ nodes, edges }),
              nodes: nodes.map(n => ({
                  node_id: n.id,
                  name: n.data.label,
                  type: n.type,
                  data: n.data, 
                  assignee: n.data.assignee,
                  logic: n.data.logic,
                  escalation: n.data.escalation,
                  action_script: n.data.action_script,
                  action_type: n.data.post_action_type,
                  action_value: n.data.post_action_value
              })),
              lines: edges.map(e => ({
                  from_node: e.source,
                  to_node: e.target,
                  source: e.source,
                  target: e.target,
                  condition_value: e.data?.result || 'NONE'
              }))
          }, { headers: { 'Authorization': `Bearer ${token}` } });
          toast.success(t('workflow.toasts.save_success'), { id: loadingToast });
      } catch (error) {
          toast.error(t('workflow.toasts.save_fail'), { id: loadingToast });
      }
    }
  };

  const executeDelete = async () => {
    const result = await Swal.fire({
      title: t('workflow.swal.delete_title'),
      text: `${t('workflow.swal.delete_text')} "${meta.name}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: t('workflow.swal.confirm_delete')
    });

    if (result.isConfirmed) {
      const loadingToast = toast.loading(t('workflow.toasts.purging'));
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`/api/workflow/process/${id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        toast.success(t('workflow.toasts.delete_success'), { id: loadingToast });
        navigate('/workflow');
      } catch (err) {
        toast.error(err.response?.data?.detail || t('workflow.toasts.delete_fail'), { id: loadingToast });
      }
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
        if ((e.ctrlKey || e.metaKey) && e.key === 's') {
            e.preventDefault();
            handlePublish();
        }
        if (e.key === 'Delete' && selectedNode) {
            setNodes((nds) => nds.filter((node) => node.id !== selectedNode.id));
            setSelectedNode(null);
        }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handlePublish, selectedNode]);

  const handleSmartSearch = (fieldName, titleKey, defaultType) => {
    setLookup({ isOpen: true, title: t(`workflow.lookups.${titleKey}`), type: defaultType, field: fieldName });
  };
  
  const handleLookupSelect = (rows) => {
    const incomingRows = Array.isArray(rows) ? rows : [rows];
    if (incomingRows.length === 0) return;

    if (lookup.field === 'action_script') {
      const scriptName = incomingRows[0].script_name;
      const currentScripts = selectedNode?.data?.action_script || "";
      const scriptsArray = currentScripts ? currentScripts.split(',').map(s => s.trim()) : [];
      if (!scriptsArray.includes(scriptName)) {
        const final = [...scriptsArray.filter(Boolean), scriptName].join(', ');
        updateNodeData('action_script', final);
      }
    } else {
      const newRolesArray = incomingRows.map(r => r.role_name || r.name || r.value).filter(Boolean);
      const currentRolesString = selectedNode?.data?.assignee || "";
      const currentRolesArray = currentRolesString ? currentRolesString.split(',').map(r => r.trim()) : [];
      const combinedRoles = Array.from(new Set([...currentRolesArray, ...newRolesArray]));
      updateNodeData('assignee', combinedRoles.join(', '));
    }
    setLookup(prev => ({ ...prev, isOpen: false }));
  };

  if (loading) return (
    <div className="h-screen w-full flex flex-col items-center justify-center bg-slate-50">
        <Zap className="animate-bounce text-blue-600 mb-4" size={48} />
        <h2 className="font-black text-slate-800 uppercase tracking-widest text-[10px]">{t('workflow.loading_designer')}</h2>
    </div>
  );

  return (
    <div className={`flex flex-col h-screen bg-[#f3f4f6] overflow-hidden font-bold ${isRTL ? 'font-cairo text-right' : 'font-sans text-left'}`} style={{ fontSize: `${fontSize}px` }}>
      <UniversalLookup 
        {...lookup} 
        onClose={() => setLookup({ ...lookup, isOpen: false })} 
        onSelect={handleLookupSelect} 
      />

      {/* --- 🛠️ 1. TOP TOOLBAR --- */}
      <div className={`bg-[#1a3353] text-white p-2 flex justify-between items-center shadow-lg z-30 px-4 shrink-0`}>
        <div className={`flex items-center gap-4 `}>
          <button onClick={() => navigate('/workflow')} className="p-1.5 hover:bg-white/10 rounded-full transition-colors text-blue-200">
            <ArrowLeft size={18} className={isRTL ? 'rotate-180' : ''} />
          </button>
          <div className="h-6 w-[1px] bg-white/10"></div>
          <div className={`flex items-center gap-2 `}>
            <Zap size={16} className="text-orange-400" fill="currentColor"/>
            <span className="text-[10px] font-black uppercase tracking-[0.2em]">{t('workflow.designer_title')}</span>
          </div>
        </div>

        <div className={`flex items-center gap-2 `}>
          <button onClick={handlePublish} className="bg-green-600 hover:bg-green-700 text-white px-5 py-1.5 rounded-sm flex items-center gap-2 text-[10px] transition-all shadow-md font-black uppercase">
            <Save size={14} /> {t('workflow.publish_btn')}
          </button>
          
          <button onClick={executeDelete} className="p-2 hover:bg-red-600/20 rounded text-red-400 transition-colors">
            <Trash2 size={16} />
          </button>

          <div className="h-6 w-[1px] bg-white/10 mx-2"></div>
          
          <div className={`flex items-center gap-2 bg-white/10 px-3 py-1 rounded-sm border border-white/20 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <button onClick={() => setFontSize(prev => prev - 1)} className="hover:text-orange-400 transition-colors"><Minus size={14} /></button>
            <div className="flex items-center gap-1 min-w-[30px] justify-center border-x border-white/10 px-2">
              <Type size={12} />
              <span className="font-black text-[10px]">{fontSize}</span>
            </div>
            <button onClick={() => setFontSize(prev => prev + 1)} className="hover:text-orange-400 transition-colors"><Plus size={14} /></button>
          </div>
        </div>
      </div>

      {/* --- 📝 2. METADATA HEADER --- */}
      <div className={`bg-white border-b border-gray-200 px-6 py-4 flex items-center gap-12 shadow-sm shrink-0`}>
        <div className={`flex justify-between items-center bg-slate-50 p-2 rounded-lg px-3 border border-slate-100`}>
            <span className={`text-[10px] font-black uppercase tracking-widest text-slate-400 ${isRTL ? 'ml-4' : 'mr-4'}`}>{t('workflow.table.status')}</span>
            <div 
                onClick={() => setMeta({...meta, isActive: !meta.isActive})}
                className={`w-12 h-6 rounded-full p-1 cursor-pointer transition-all duration-300 ${meta.isActive ? 'bg-green-500' : 'bg-slate-300'}`}
            >
                <div className={`bg-white w-4 h-4 rounded-full shadow-md transform transition-transform ${meta.isActive ? (isRTL ? '-translate-x-0' : 'translate-x-6') : (isRTL ? '-translate-x-6' : 'translate-x-0')}`}></div>
            </div>
        </div>

        <div className={`flex flex-col min-w-[200px] ${isRTL ? 'text-right' : 'text-left'}`}>
          <label className="text-[9px] text-gray-400 font-black uppercase tracking-widest mb-1">{t('workflow.table.name')}</label>
          <input 
            className="text-sm font-black text-[#1a3353] outline-none uppercase bg-transparent border-b-2 border-transparent focus:border-blue-600 transition-all"
            value={meta.name}
            onChange={e => setMeta({...meta, name: e.target.value.toUpperCase()})}
            placeholder="PROCESS_ID"
          />
        </div>

        <div className={`flex flex-col flex-1 border-gray-100 ${isRTL ? 'border-r pr-8' : 'border-l pl-8'}`}>
          <label className="text-[9px] text-gray-400 font-black uppercase tracking-widest mb-1">{t('organizations.description')}</label>
          <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <AlignLeft size={16} className="text-slate-300"/>
            <input 
              className="text-sm font-bold text-gray-600 outline-none focus:text-blue-600 bg-transparent w-full"
              value={meta.description}
              onChange={e => setMeta({...meta, description: e.target.value})}
              placeholder="..."
            />
          </div>
        </div>

        <div className={`flex flex-col min-w-[150px] border-gray-100 ${isRTL ? 'border-r pr-8' : 'border-l pl-8'}`}>
          <label className="text-[9px] text-gray-400 font-black uppercase tracking-widest mb-1">{t('workflow.table.app')}</label>
          <select 
            className="text-[10px] font-black text-orange-600 outline-none bg-orange-50 px-3 py-1 rounded-sm border border-orange-100 cursor-pointer"
            value={meta.app}
            onChange={e => setMeta({...meta, app: e.target.value})}
          >
            <option value="WORKORDER">WORKORDER</option>
            <option value="PURCHASE">PURCHASE</option>
            <option value="PR">PURCHASE REQUEST</option>
            <option value="MR">MATERIAL REQUEST</option>
            <option value="SR">SERVICE REQUEST</option>
            <option value="INCIDENT">INCIDENT</option>
            <option value="PO">PO</option>
          </select>
        </div>
      </div>

      {/* --- 🏗️ 3. DESIGNER WORKSPACE --- */}
      <div className={`flex flex-1 overflow-hidden`}>
        {/* PALETTE */}
        <div className={`w-64 bg-white p-5 flex flex-col gap-5 shadow-xl z-20 overflow-y-auto ${isRTL ? 'border-l' : 'border-r'}`}>
          <h3 className="font-black text-[10px] text-gray-400 uppercase tracking-widest mb-2 border-b pb-3">{t('workflow.logic_palette')}</h3>
          {['start', 'task', 'condition', 'end'].map((type) => (
            <div 
              key={type} 
              onClick={() => onAddNode(type)} 
              className={`p-4 bg-slate-50 border-2 border-slate-100 rounded-lg cursor-pointer hover:border-blue-600 hover:bg-blue-50 transition-all flex items-center gap-4 group shadow-sm ${isRTL ? 'flex-row-reverse text-right' : ''}`}
            >
              <div className={`w-8 h-8 rounded-sm flex items-center justify-center text-white shadow-md ${type==='start'?'bg-green-600':type==='end'?'bg-red-600':type==='condition'?'bg-orange-500':'bg-blue-600'}`}>
                {type === 'start' ? <Play size={14}/> : type === 'condition' ? <GitBranch size={14}/> : <UserCheck size={14}/>}
              </div>
              <span className="text-[10px] font-black uppercase text-slate-700 tracking-tight">{type} node</span>
            </div>
          ))}
        </div>

        {/* CANVAS */}
        <div className="flex-1 relative bg-[#f8f9fb]" ref={reactFlowWrapper}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={(_, node) => { setSelectedNode(node); setSelectedEdge(null); }}
            onEdgeClick={(_, edge) => { setSelectedEdge(edge); setSelectedNode(null); }}
            nodeTypes={nodeTypes}
            connectionMode={ConnectionMode.Loose} 
            fitView
          >
            <Background color="#cbd5e1" gap={20} variant="dots" />
            <Controls />
            <MiniMap maskColor="rgba(26, 51, 83, 0.1)" />
          </ReactFlow>
        </div>

        {/* PROPERTIES PANEL */}
        <div className={`w-80 bg-white shadow-2xl p-8 overflow-y-auto z-20 ${isRTL ? 'border-r' : 'border-l'}`}>
          {selectedNode ? (
            <div className="animate-in slide-in-from-right duration-300">
              <h3 className={`font-black text-[#1a3353] uppercase border-b-2 border-blue-600 pb-3 mb-8 flex items-center gap-3 text-[12px] tracking-widest`}>
                  <Settings size={18} className="text-blue-600"/> {t('workflow.config_title')}
              </h3>
              <div className="space-y-8 text-slate-700">
                <div className="flex flex-col gap-2">
                  <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest">{t('workflow.node_label')}</label>
                  <input className={`border-b-2 border-gray-100 p-2 text-sm outline-none focus:border-blue-600 font-bold ${isRTL ? 'text-right' : ''}`} value={selectedNode.data.label} onChange={(e) => updateNodeData('label', e.target.value)} />
                </div>
                
                {selectedNode.type === 'task' && (
                  <>
                    <div className="flex flex-col gap-2">
                      <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest">{t('workflow.assign_roles')}</label>
                      <div className={`relative flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <input readOnly className={`w-full border-b-2 border-blue-100 p-2 outline-none bg-slate-50 font-bold text-blue-900 text-[10px] ${isRTL ? 'text-right' : ''}`} value={selectedNode.data.assignee || ''} />
                        <button onClick={() => handleSmartSearch('assignee', 'select_roles', 'roles')} className={`absolute ${isRTL ? 'left-0' : 'right-0'} p-2 text-blue-600 hover:text-blue-800 transition-colors`}><Search size={16} /></button>
                      </div>
                    </div>

                    <div className="bg-blue-50/50 p-4 rounded-lg border border-blue-100 mt-6 shadow-sm">
                      <label className={`text-[9px] font-black text-blue-600 uppercase flex items-center gap-2 mb-3 tracking-widest ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <FileCode size={14} fill="currentColor"/> {t('workflow.post_action')}
                      </label>
                      <div className="relative flex flex-col gap-2">
                        <textarea 
                          className={`w-full bg-white border border-blue-200 rounded p-3 text-[10px] font-mono font-bold outline-none h-24 shadow-inner ${isRTL ? 'text-right' : ''}`}
                          value={selectedNode.data.action_script || ""} 
                          onChange={(e) => updateNodeData('action_script', e.target.value)} 
                          placeholder="..."
                        />
                        <button 
                          onClick={() => handleSmartSearch('action_script', 'select_scripts', 'scripts')}
                          className="mt-2 bg-blue-600 text-white py-1.5 rounded flex items-center justify-center gap-2 text-[9px] font-black hover:bg-black transition-all"
                        >
                          <Plus size={12}/> {t('workflow.select_lookup')}
                        </button>
                      </div>
                      <p className={`text-[8px] text-blue-400 mt-2 italic ${isRTL ? 'text-right' : ''}`}>{t('workflow.script_hint')}</p>
                    </div>

                    <div className="bg-purple-50 p-4 rounded-lg border border-purple-100 mt-6 shadow-sm">
                      <label className={`text-[10px] font-black text-purple-600 uppercase flex items-center gap-2 mb-4 tracking-widest ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <History size={14} fill="currentColor"/> {t('workflow.status_auto')}
                      </label>
                      <div className="space-y-4">
                        <div className="flex flex-col gap-1">
                          <span className="text-[8px] font-black text-purple-300 uppercase">{t('workflow.trigger')}</span>
                          <select 
                            className={`w-full p-2 bg-white border border-purple-200 rounded text-[10px] font-bold outline-none ${isRTL ? 'text-right' : ''}`}
                            value={selectedNode.data.post_action_type || ''}
                            onChange={(e) => updateNodeData('post_action_type', e.target.value)}
                          >
                            <option value="">{t('workflow.options.no_change')}</option>
                            <option value="STATUS_CHANGE">{t('workflow.options.change_status')}</option>
                          </select>
                        </div>
                        {selectedNode.data.post_action_type === 'STATUS_CHANGE' && (
                          <div className="flex flex-col gap-1">
                             <span className="text-[8px] font-black text-purple-300 uppercase">{t('workflow.target_status')}</span>
                             <select 
                                className={`w-full p-2 bg-white border border-purple-200 rounded text-[10px] font-bold outline-none ${isRTL ? 'text-right' : ''}`}
                                value={selectedNode.data.post_action_value || ''}
                                onChange={(e) => updateNodeData('post_action_value', e.target.value)}
                              >
                                <option value="">{t('workflow.options.select_status')}</option>
                                <option value="APPR">APPROVED</option>
                                <option value="INPRG">IN PROGRESS</option>
                                <option value="COMP">COMPLETED</option>
                                <option value="WAPPR">WAITING APPR</option>
                              </select>
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}

                {selectedNode.type === 'condition' && (
                  <div className={`flex flex-col gap-3 pt-2 ${isRTL ? 'text-right' : ''}`}>
                    <label className="text-[10px] font-black text-orange-500 uppercase italic tracking-widest">{t('workflow.cond_expr')}</label>
                    <textarea 
                      className="bg-slate-900 text-green-400 p-4 rounded-lg font-mono text-[11px] h-48 outline-none border-2 border-orange-500/20 shadow-xl" 
                      value={selectedNode.data.logic} 
                      onChange={(e) => updateNodeData('logic', e.target.value)} 
                      placeholder="if record.total_cost > 5000: result = True" 
                    />
                  </div>
                )}
                
                <div className={`flex flex-col gap-2 pt-2 ${isRTL ? 'text-right' : ''}`}>
                  <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest">{t('workflow.escalation')}</label>
                  <input type="number" className={`border-b-2 border-gray-100 p-2 text-sm outline-none font-bold ${isRTL ? 'text-right' : ''}`} value={selectedNode.data.escalation} onChange={(e) => updateNodeData('escalation', e.target.value)} />
                </div>
              </div>
            </div>
          ) : (
            <div className="h-full flex flex-col items-center justify-center opacity-10 text-center select-none grayscale">
              <Settings size={80} strokeWidth={1} />
              <p className="font-black text-[10px] uppercase tracking-[0.3em] mt-4">{t('workflow.select_node')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkflowDesigner;